import { CSSObject } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Position } from './types';

type Area = 'left' | 'right' | 'top' | 'bottom';
type Alignment = 'left' | 'center' | 'right' | 'top' | 'bottom';

const DEFAULT_POSITION: [Area, Alignment] = ['top', 'left'] as const;
const COMMON_STYLES: CSSObject = {
  display: 'flex',
  position: 'absolute',
  transition: 'transform 0.2s ease-in-out',
  zIndex: theme.zIndex.middle,
} as const;

/**
 * Generates CSS styles for positioning a section toolbar based on a position string.
 *
 * The position string follows the format `{area}-{alignment}` where:
 * - Area can be: 'left', 'right', 'top', or 'bottom'
 * - Alignment varies based on the area:
 *   - For left/right areas: 'top', 'center', or 'bottom'
 *   - For top/bottom areas: 'left', 'center', or 'right'
 *
 * @param position - The position string (e.g., 'left-center', 'top-right')
 * @returns CSS object with positioning styles
 */
export const getPositionStyles = (position: Position): CSSObject => {
  const [area, alignment] = parsePosition(position);

  // Left area
  if (area === 'left') {
    const leftStyles: CSSObject = {
      ...COMMON_STYLES,
      alignItems: 'flex-end',
      flexDirection: 'column',
      left: 0,
      rowGap: theme.spacing(1),
    };

    // Center alignment
    if (alignment === 'center') {
      return {
        ...leftStyles,
        top: '50%',
        transform: 'translate(-100%, -50%)',
      };
    }

    // Bottom alignment
    if (alignment === 'bottom') {
      return {
        ...leftStyles,
        bottom: 0,
        marginBottom: '-2px', // Compensate for border width
        transform: 'translateX(-100%)',
      };
    }

    // Default to top alignment
    return {
      ...leftStyles,
      marginTop: '-2px', // Compensate for border width
      top: 0,
      transform: 'translateX(-100%)',
    };
  }

  // Right area
  if (area === 'right') {
    const rightStyles: CSSObject = {
      ...COMMON_STYLES,
      alignItems: 'flex-start',
      flexDirection: 'column',
      right: 0,
      rowGap: theme.spacing(1),
    };

    // Center alignment
    if (alignment === 'center') {
      return {
        ...rightStyles,
        top: '50%',
        transform: 'translate(100%, -50%)',
      };
    }

    // Bottom alignment
    if (alignment === 'bottom') {
      return {
        ...rightStyles,
        bottom: 0,
        marginBottom: theme.spacing(3),
        transform: 'translateX(100%)',
      };
    }

    // Default to top alignment
    return {
      ...rightStyles,
      marginTop: theme.spacing(3),
      top: 0,
      transform: 'translateX(100%)',
    };
  }

  // Bottom area
  if (area === 'bottom') {
    const bottomStyles: CSSObject = {
      ...COMMON_STYLES,
      columnGap: theme.spacing(1),
      flexDirection: 'row',
    };

    // Center alignment
    if (alignment === 'center') {
      return {
        ...bottomStyles,
        bottom: 0,
        justifyContent: 'center',
        left: '50%',
        transform: 'translate(-50%, 50%)',
      };
    }

    // Right alignment
    if (alignment === 'right') {
      return {
        ...bottomStyles,
        bottom: 0,
        justifyContent: 'flex-end',
        marginRight: theme.spacing(-2),
        right: 0,
        transform: 'translateY(50%)',
      };
    }

    // Default to left alignment
    return {
      ...bottomStyles,
      bottom: 0,
      justifyContent: 'flex-start',
      left: 0,
      marginLeft: theme.spacing(1),
      transform: 'translateY(50%)',
    };
  }

  // Default to top area
  const topStyles: CSSObject = {
    ...COMMON_STYLES,
    columnGap: theme.spacing(1),
    flexDirection: 'row',
  };

  // Center alignment
  if (alignment === 'center') {
    return {
      ...topStyles,
      justifyContent: 'center',
      left: '50%',
      top: 0,
      transform: 'translate(-50%, -50%)',
    };
  }

  // Right alignment
  if (alignment === 'right') {
    return {
      ...topStyles,
      justifyContent: 'flex-end',
      marginRight: theme.spacing(-2),
      right: 0,
      top: 0,
      transform: 'translateY(-50%)',
    };
  }

  // Default to left alignment
  return {
    ...topStyles,
    justifyContent: 'flex-start',
    left: 0,
    marginLeft: theme.spacing(1),
    top: 0,
    transform: 'translateY(-50%)',
  };
};

const parsePosition = (position?: Position): [Area, Alignment] => {
  if (!position || typeof position !== 'string') {
    return DEFAULT_POSITION;
  }

  const parts = position.split('-');

  if (parts.length !== 2) {
    return DEFAULT_POSITION;
  }

  const area = isArea(parts[0]) ? parts[0] : DEFAULT_POSITION[0];
  const alignment = isAlignment(parts[1]) ? parts[1] : DEFAULT_POSITION[1];

  return [area, alignment];
};

const AREA_VALUES: readonly Area[] = ['left', 'right', 'top', 'bottom'] as const;
const isArea = (area?: string): area is Area => {
  return !!area && AREA_VALUES.includes(area as Area);
};

const ALIGNMENT_VALUES: readonly Alignment[] = ['left', 'center', 'right', 'top', 'bottom'] as const;
const isAlignment = (alignment?: string): alignment is Alignment => {
  return !!alignment && ALIGNMENT_VALUES.includes(alignment as Alignment);
};
